import { useEffect, useRef } from 'react';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';

import { setSidebarOpen, useSidebarOpen } from '../components/agent/agent-sidebar-store';
import { useStoredThread } from '../components/agent/agent-thread-store';
import { useAuth } from './useAuth';

export function useAgentSidebar() {
  const open = useSidebarOpen();
  const [urlState, setUrlState] = useQueryState(
    'agent-sidebar',
    parseAsStringLiteral(['open', 'closed'] as const).withOptions({ history: 'replace' }),
  );

  const adoptedRef = useRef(false);
  useEffect(() => {
    if (adoptedRef.current || !urlState) return;
    adoptedRef.current = true;
    setSidebarOpen(urlState === 'open');
    void setUrlState(null);
  }, [urlState, setUrlState]);

  return {
    open,
    setOpen: setSidebarOpen,
    toggle: () => setSidebarOpen(!open),
  };
}

export function useAgentShortcuts(): void {
  const { open, setOpen, toggle } = useAgentSidebar();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggle();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen, toggle]);
}

export function useAgentThread() {
  const { organization } = useAuth();
  const organizationId = organization?.id;
  const { threadId: stored, setThreadId: setStored } = useStoredThread(organizationId);
  const [urlThreadId, setUrlThreadId] = useQueryState(
    'agent-thread',
    parseAsString.withOptions({ history: 'replace' }),
  );

  const adoptedRef = useRef(false);
  useEffect(() => {
    if (adoptedRef.current || !organizationId || !urlThreadId) return;
    adoptedRef.current = true;
    setStored(urlThreadId);
    void setUrlThreadId(null);
  }, [organizationId, urlThreadId, setStored, setUrlThreadId]);

  return {
    threadId: stored,
    setThreadId: setStored,
  };
}
